import { alpha, SxProps, Theme } from "@mui/material";

export const MMK_PERSONAL_CARD_DIALOG_Z_INDEX = 2000;
export const MMK_PERSONAL_CATEGORY_MENU_Z_INDEX = 3000;

export const dialogRootSlotSx: SxProps<Theme> = {
  zIndex: MMK_PERSONAL_CARD_DIALOG_Z_INDEX,
};

export const dialogPaperSx: SxProps<Theme> = {
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  m: { xs: 1, sm: 2 },
  width: { xs: "calc(100% - 16px)", sm: "100%" },
  maxWidth: { xs: "100%", sm: 600 },
  maxHeight: {
    xs: "calc(100dvh - 16px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
    sm: "90vh",
  },
  borderRadius: { xs: 2, sm: 3 },
};

export const dialogTitleSx: SxProps<Theme> = {
  pt: { xs: 2, sm: 2.5 },
  pb: 1,
  px: { xs: 2, sm: 2.5 },
  fontWeight: 700,
  fontSize: { xs: "1.125rem", sm: "1.25rem" },
  letterSpacing: "-0.01em",
};

export const dialogContentSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1.75, sm: 2 },
  pt: 0,
  px: { xs: 2, sm: 2.5 },
  pb: { xs: 2.5, sm: 2 },
  overflowY: "auto",
  overflowX: "hidden",
  flex: "1 1 auto",
  minHeight: 0,
  WebkitOverflowScrolling: "touch",
};

export const flashcardPanelOuterSx: SxProps<Theme> = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
};

export const flashcardPanelHeaderSx = (theme: Theme): SxProps<Theme> => ({
  bgcolor: theme.palette.mode === "dark" ? "grey.800" : "#f0f0f0",
  py: { xs: 1, sm: 0.75 },
  px: { xs: 1.25, sm: 1.5 },
  borderRadius: "8px 8px 0 0",
});

export const flashcardPanelHeaderTitleSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.mode === "dark" ? "grey.100" : "grey.900",
});

export const flashcardPanelBodySx = (
  highlighted: boolean,
  theme: Theme,
): SxProps<Theme> => ({
  p: { xs: "12px 12px 16px", sm: 2 },
  overflow: "visible",
  borderRadius: "0 0 8px 8px",
  bgcolor:
    highlighted && theme.palette.mode === "light"
      ? alpha(theme.palette.primary.main, 0.08)
      : highlighted
        ? alpha(theme.palette.primary.main, 0.12)
        : "background.paper",
});

export const characterCountRowSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 1,
  width: "100%",
};

export const characterCountHintCaptionSx: SxProps<Theme> = {
  wordBreak: "break-word",
  pr: 0.5,
};

export const characterCountNumberSx: SxProps<Theme> = {
  flexShrink: 0,
};

export const fieldLabelSx: SxProps<Theme> = {
  mb: 1,
  fontWeight: 500,
};

export const requiredFieldMarkerSx: SxProps<Theme> = {
  color: "primary.main",
  ml: 0.25,
};

export const textFieldOutlineOverflowSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": { overflow: "visible" },
};

// ≥16px on small screens avoids iOS zoom-on-focus on inputs
export const inputFontSx: SxProps<Theme> = {
  fontSize: { xs: "1rem", sm: undefined },
  lineHeight: { xs: 1.5, sm: undefined },
};

export const textFieldInputRootSx: SxProps<Theme> = {
  bgcolor: "background.paper",
  borderRadius: 1.5,
  ...inputFontSx,
};

export const textFieldEndAdornmentSx: SxProps<Theme> = {
  alignSelf: "flex-start",
  mt: 1,
  mr: 0.25,
};

export const textFieldEditIconSx: SxProps<Theme> = {
  color: "action.active",
  opacity: 0.65,
};

export const formHelperTextSlotSx: SxProps<Theme> = {
  mx: 0,
  mt: 0.75,
  mb: { xs: 0.25, sm: 0 },
};

export const categoryRowSx: SxProps<Theme> = {
  display: "flex",
  gap: 1,
  alignItems: "stretch",
};

export const categoryFormControlSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const categorySelectSx: SxProps<Theme> = {
  borderRadius: 1.5,
  bgcolor: "background.paper",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "divider",
  },
};

export const categoryOtherButtonSx: SxProps<Theme> = {
  flexShrink: 0,
  whiteSpace: "nowrap",
  borderRadius: 1.5,
  textTransform: "none",
  px: { xs: 1.25, sm: 1.75 },
  minHeight: { xs: 48, sm: 40 },
  borderColor: "divider",
  color: "text.primary",
};

export const personalCardsHintSx: SxProps<Theme> = {
  mt: -0.5,
};

export const dialogActionsSx: SxProps<Theme> = {
  px: { xs: 2, sm: 2.5 },
  pt: 1,
  pb: {
    xs: "max(16px, env(safe-area-inset-bottom, 0px))",
    sm: 2.5,
  },
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: { xs: 1, sm: 2 },
};

export const cancelButtonSx: SxProps<Theme> = {
  textTransform: "none",
  textDecoration: "underline",
  color: "text.secondary",
  minHeight: { xs: 44, sm: 36 },
  px: { xs: 0.5, sm: 1 },
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
};

export const primarySubmitButtonSx = (theme: Theme): SxProps<Theme> => {
  const primaryBg =
    theme.palette.mode === "light" ? "#0d47a1" : theme.palette.primary.dark;
  const primaryHover =
    theme.palette.mode === "light" ? "#0a3d8a" : theme.palette.primary.main;
  return {
    borderRadius: 2,
    px: { xs: 2, sm: 2.5 },
    py: { xs: 1.25, sm: 1 },
    minHeight: { xs: 48, sm: 40 },
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    bgcolor: primaryBg,
    color: "#fff",
    "&:hover": {
      boxShadow: "none",
      bgcolor: primaryHover,
    },
    "&.Mui-disabled": {
      color: theme.palette.text.disabled,
      bgcolor: theme.palette.action.disabledBackground,
    },
    flex: { xs: "1 1 auto", sm: "0 0 auto" },
    minWidth: { xs: 140, sm: 120 },
  };
};

export const categorySelectMenuProps = {
  disablePortal: false,
  slotProps: {
    root: {
      sx: { zIndex: MMK_PERSONAL_CATEGORY_MENU_Z_INDEX },
    },
    paper: {
      sx: { maxHeight: 320 },
    },
  },
} as const;
